import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useUpdateOrgBilingAddressMutation } from 'store/apis/org.api';

interface UseShippingAddressFormManagerProps {
  next?: (data?: any) => void;
}

export const useShippingAddressFormManager = ({ next = () => {} }: UseShippingAddressFormManagerProps) => {
  const { orgSlug, billingAddress: billing, shippingAddress: shipping } = useAppSelector(selectCurrentUserInfo);
  const [updateShippingAddress, { isLoading, isSuccess, isError, ...rest }] = useUpdateOrgBilingAddressMutation();

  const initialValues = {
    shipping_address1: shipping?.address_1 || '',
    shipping_address2: shipping?.address_2 || '',
    shipping_city: shipping?.city || '',
    shipping_state: shipping?.state || '',
    shipping_zip: shipping?.zip || '',
  };

  const validationSchema = Yup.object({
    shipping_address1: Yup.string().nullable().required('Shipping address is required.'),
    shipping_city: Yup.string().nullable().required('Shipping city is required.'),
    shipping_state: Yup.string().nullable().required('Shipping state is required.'),
    shipping_zip: Yup.string()
      .nullable()
      .required('Shipping zip code is required.')
      .matches(/\b\d{5}\b/, { message: 'Zip code must be 5 digits.', excludeEmptyString: true }),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { shipping_address1, shipping_address2, shipping_city, shipping_state, shipping_zip } = values;

      const shipping_address = {
        id: null,
        address_1: shipping_address1,
        address_2: shipping_address2,
        city: shipping_city,
        state: shipping_state,
        zip: shipping_zip,
      };

      const billing_address = billing || null;

      updateShippingAddress({
        orgSlug,
        billing_address,
        shipping_address,
      });
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;

      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [
    formManager,
    {
      isLoading,
      isSuccess,
      isError,
    },
  ] as [
    FormikProps<typeof initialValues>,
    {
      isLoading: boolean;
      isSuccess: boolean;
      isError: boolean;
    },
  ];
};
